import { lazy, Suspense, Fragment, useMemo } from 'react';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import Image from 'components/Image';
import { useScrollRestore, useRouteTransition } from 'hooks';
import { Button } from 'components/Button';
import Footer from 'components/Footer';
import {
  ProjectContainer,
  ProjectSection,
  ProjectSectionContent,
  ProjectImage,
  ProjectBackground,
  ProjectHeader,
  ProjectSectionHeading,
  ProjectSectionText,
  ProjectTextRow,
  ProjectSectionColumns,
} from 'components/ProjectLayout';
import { useTheme } from 'components/ThemeProvider';
import dotblueBackground from 'assets/dotblue.jpg';
import dotblueBackgroundLarge from 'assets/dotblueLarge.jpg';
import dotblueBackgroundPlaceholder from 'assets/dotbluePlace.jpg';
import volkiharBanner from 'assets/volkihar-banner.jpg';
import volkiharBannerLarge from 'assets/volkihar-banner-large.jpg';
import volkiharBannerPlaceholder from 'assets/volkihar-banner-placeholder.jpg';
import volkiharArmor from 'assets/EdgarAtdc.png';
import volkiharArmorLarge from 'assets/EdgarAtdc.png';
import volkiharArmorPlaceholder from 'assets/EdgarAtdc.png';
import volkiharBook from 'assets/volkihar-book.png';
import volkiharBookLarge from 'assets/volkihar-book-large.png';
import volkiharBookPlaceholder from 'assets/volkihar-book-placeholder.png';
import volkiharEnderal from 'assets/volkihar-enderal.jpg';
import volkiharEnderalLarge from 'assets/volkihar-enderal-large.jpg';
import volkiharEnderalPlaceholder from 'assets/volkihar-enderal-placeholder.jpg';
import volkiharEnderalLogo from 'assets/volkihar-enderal-logo.png';
import volkiharEnderalLogoLarge from 'assets/volkihar-enderal-logo-large.png';
import volkiharEnderalLogoPlaceholder from 'assets/volkihar-enderal-logo-placeholder.png';
import volkiharSlide1 from 'assets/volkihar-slide-1.jpg';
import volkiharSlide1Large from 'assets/volkihar-slide-1-large.jpg';
import volkiharSlide2 from 'assets/volkihar-slide-2.jpg';
import volkiharSlide2Large from 'assets/volkihar-slide-2-large.jpg';
import volkiharSlide3 from 'assets/volkihar-slide-3.jpg';
import volkiharSlide3Large from 'assets/volkihar-slide-3-large.jpg';
import volkiharSlidePlaceholder from 'assets/volkihar-slide-placeholder.jpg';
import { ReactComponent as VolkiharKnightLogo } from 'assets/volkihar-logo.svg';
import prerender from 'utils/prerender';
import { media } from 'utils/style';
import './index.css';

const Carousel = lazy(() => import('components/Carousel'));

const title = 'Skills';
const description =
  '"Never stop learning", as at Platzi often says, it is the key to success in tech industries';
const roles = ['HTML + CSS + JavaScript', 'Wordpress & React', 'Git + GitHub'];

function EdgarSkills() {
  const { themeId } = useTheme();
  const { status } = useRouteTransition();
  useScrollRestore();

  return (
    <Fragment>
      <Helmet>
        <title>{`Projects | ${title}`}</title>
        <meta name="description" content={description} />
      </Helmet>
      {(status === 'entered' || status === 'exiting') && (
        <style>{`
          .dark {
            --rgbPrimary: 240 211 150;
            --rgbAccent: 240 211 150;
          }
          .light {
            --rgbAccent: 240 211 150;
          }
        `}</style>
      )}
      <ProjectContainer className="volkihar">
        <ProjectBackground
          srcSet={`${dotblueBackground} 1000w, ${dotblueBackgroundLarge} 1600w`}
          placeholder={dotblueBackgroundPlaceholder}
          opacity={0.5}
          entered={!prerender}
        />
        <ProjectHeader
          title={title}
          description={description}
          linkLabel="What is Platzi?"
          url="https://platzi.com/"
          roles={roles}
        />

        <ProjectSection>
          <ProjectSectionColumns>
            <Image
              srcSet={`${volkiharArmor} 400w, ${volkiharArmorLarge} 800w`}
              placeholder={volkiharArmorPlaceholder}
              alt="A 3D render of the full suit of armor."
              sizes={`(max-width: ${media.mobile}px) 100vw, (max-width: ${media.tablet}px) 100vw, 50vw`}
            />
            <div className="volkihar__text-section">
              <ProjectSectionHeading>A Pale Blue Dot</ProjectSectionHeading>
              <ProjectSectionText>
                Acquiring the skills to develop software takes a lot of time and effort,
                which sometimes means losing a bit of humility. That makes the
                qualification levels difficult to understand, there is no standard test
                that determines if you are a junior, mid or senior developer. So you can
                measure my skills by checking that most of our projects are at the top of
                the search engine results, are Gtmetrix grade A and have a pretty nice
                interface design.
              </ProjectSectionText>

              <ProjectSectionText>
                For those people who sometimes lose their sense of reality, I leave these
                photos, behind me is the most powerful democracy humanity has seen, which
                is impressive, however, it exists on a small planet that from a distance
                looks like a pale blue dot
              </ProjectSectionText>

              <ProjectSectionText>
                "It has been said that astronomy is a humbling and character-building
                experience. There is perhaps no better demonstration of the folly of human
                conceits than this distant image of our tiny world. To me, it underscores
                our responsibility to deal more kindly with one another, and to preserve
                and cherish the pale blue dot, the only home we've ever known..." Carl
                Sagan
              </ProjectSectionText>
            </div>
          </ProjectSectionColumns>
        </ProjectSection>
      </ProjectContainer>
      <Footer />
    </Fragment>
  );
}

export default EdgarSkills;
